import React, { useEffect, useState } from 'react';
import { Check, Pencil, Plus, X } from 'lucide-react';

import { payrollApi } from '../../services/api';
import type { ShiftType } from '../../types';
import { Button, Input, LoadingState, Modal, StatusBadge, useToast } from '../ui';

// Kinds of shifts a class can be paid as. Types are never deleted, only retired, so past shifts keep their pay.
export default function ShiftTypesDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const { showToast } = useToast();
    const [types, setTypes] = useState<ShiftType[] | null>(null);
    const [name, setName] = useState('');
    const [minutes, setMinutes] = useState('');
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);

    useEffect(() => {
        if (!open) return;
        setTypes(null);
        payrollApi.getShiftTypes()
            .then(response => setTypes(response.data.data))
            .catch((error: any) => {
                showToast(error?.message || 'Không tải được loại ca.', 'error');
                setTypes([]);
            });
    }, [open, showToast]);

    if (!open) return null;

    const run = async (action: () => Promise<{ data: { data: ShiftType[] } }>, success: string) => {
        setSaving(true);
        try {
            const response = await action();
            setTypes(response.data.data);
            showToast(success, 'success');
            return true;
        } catch (error: any) {
            showToast(error?.message || 'Lưu loại ca thất bại.', 'error');
            return false;
        } finally {
            setSaving(false);
        }
    };

    const add = async (event: React.FormEvent) => {
        event.preventDefault();
        const length = Number(minutes);
        if (!name.trim() || !Number.isInteger(length) || length < 15) {
            showToast('Nhập tên và số phút (từ 15 phút).', 'error');
            return;
        }
        if (await run(() => payrollApi.createShiftType({ name: name.trim(), minutes: length }), `Đã thêm ${name.trim()}.`)) {
            setName('');
            setMinutes('');
        }
    };

    const rename = async () => {
        if (!editing || !editing.name.trim()) return;
        if (await run(() => payrollApi.updateShiftType(editing.id, { name: editing.name.trim() }), 'Đã đổi tên loại ca.')) {
            setEditing(null);
        }
    };

    return (
        <Modal
            open
            title="Loại ca"
            description="Mỗi lớp được trả lương theo một loại ca. Loại ca ngừng dùng vẫn giữ cho các ca đã dạy."
            onClose={() => !saving && onClose()}
            footer={<div className="flex justify-end"><Button variant="secondary" onClick={onClose} disabled={saving}>Đóng</Button></div>}
        >
            {!types ? <LoadingState title="Đang tải loại ca" /> : (
                <div className="space-y-4">
                    <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200">
                        {types.map(type => (
                            <li key={type.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                                {editing?.id === type.id ? (
                                    <div className="flex min-w-0 flex-1 items-center gap-1">
                                        <input
                                            aria-label={`Tên mới cho ${type.name}`}
                                            className="min-h-9 min-w-0 flex-1 rounded-md border border-ink-300 px-2 text-sm"
                                            value={editing.name}
                                            autoFocus
                                            onChange={event => setEditing({ id: type.id, name: event.target.value })}
                                            onKeyDown={event => {
                                                if (event.key === 'Enter') rename();
                                                if (event.key === 'Escape') setEditing(null);
                                            }}
                                        />
                                        <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Lưu tên" icon={<Check className="h-4 w-4" />} onClick={rename} disabled={saving} />
                                        <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Huỷ" icon={<X className="h-4 w-4" />} onClick={() => setEditing(null)} disabled={saving} />
                                    </div>
                                ) : (
                                    <div className="min-w-0">
                                        <p className={type.isActive ? 'font-medium text-ink-900' : 'font-medium text-ink-500'}>{type.name}</p>
                                        <p className="text-xs text-ink-500">{type.minutes} phút</p>
                                    </div>
                                )}
                                <div className="flex shrink-0 items-center gap-1">
                                    {!type.isActive ? <StatusBadge tone="neutral" icon={null}>Ngừng dùng</StatusBadge> : null}
                                    {editing?.id !== type.id ? (
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            className="h-8 w-8"
                                            aria-label={`Đổi tên ${type.name}`}
                                            icon={<Pencil className="h-4 w-4" />}
                                            onClick={() => setEditing({ id: type.id, name: type.name })}
                                            disabled={saving}
                                        />
                                    ) : null}
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        disabled={saving}
                                        onClick={() => run(
                                            () => payrollApi.updateShiftType(type.id, { isActive: !type.isActive }),
                                            type.isActive ? `Đã ngừng dùng ${type.name}.` : `Đã dùng lại ${type.name}.`,
                                        )}
                                    >
                                        {type.isActive ? 'Ngừng dùng' : 'Dùng lại'}
                                    </Button>
                                </div>
                            </li>
                        ))}
                    </ul>

                    <form onSubmit={add} className="space-y-3 rounded-lg border border-ink-200 bg-ink-50 p-4">
                        <p className="text-sm font-semibold text-ink-900">Thêm loại ca</p>
                        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_8rem_auto] sm:items-end">
                            <Input label="Tên" placeholder="Ca 60 phút" value={name} onChange={event => setName(event.target.value)} disabled={saving} />
                            <Input label="Số phút" inputMode="numeric" placeholder="60" value={minutes} onChange={event => setMinutes(event.target.value.replace(/\D/g, ''))} disabled={saving} />
                            <Button type="submit" loading={saving} icon={<Plus className="h-4 w-4" />}>Thêm</Button>
                        </div>
                        <p className="text-xs text-ink-500">Lớp mới được tự gán loại ca có số phút bằng thời lượng buổi học; đổi loại ca của lớp trong chi tiết lớp.</p>
                    </form>
                </div>
            )}
        </Modal>
    );
}
